import { isEqual } from 'es-toolkit';
import React, { memo, useState } from 'react';
import { Html } from '@react-three/drei';
import { Vector3 } from 'three';

import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { Direction2D, Point2D, Vector2D } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';

import { useAppSelector } from '../../components/hooks';
import { getDistanceFormatterSelector } from '../../store/selectors/getDistanceFormatterSelector';
import { inViewOnlyMode } from '../../store/selectors/viewOnlyModeSelectors';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { LEFT_MOUSE_BUTTON, MIDDLE_MOUSE_BUTTON } from '../util/events/util';
import { DistanceDimension } from './DistanceDimension';

interface Props {
  readonly start: Point2DInterface;
  readonly end: Point2DInterface;
  readonly distance: number;
  readonly showEndpointMarks?: boolean;
  readonly onChange: (value: number) => void;
  readonly textFloat?: number;
}

export const EditableDistanceDimension = memo(
  ({ start, end, distance, showEndpointMarks, onChange, textFloat }: Props) => {
    const distanceFormatter = useAppSelector(getDistanceFormatterSelector);
    const viewOnly = useAppSelector(inViewOnlyMode);
    const distanceText = distanceFormatter(distance);
    const [currentText, setCurrentText] = useState(distanceText);

    const startPoint = Point2D.fromObject(start);
    const endPoint = Point2D.fromObject(end);
    const direction = Direction2D.fromPoints(startPoint, endPoint);
    const offset =
      textFloat != null && direction.hasDirection()
        ? Vector2D.fromDirectionAndMagnitude(direction.rotateQuarterTurn(), textFloat)
        : Vector2D.zero;

    const position = new Point2D((start.x + end.x) / 2, (start.y + end.y) / 2).translate(offset);
    return (
      <>
        <DistanceDimension
          segment={{ start, end }}
          labelData={viewOnly ? { fontSize: 2, firstNumber: distance, applyAntiZoomFactor: true } : undefined}
          distanceFormatter={distanceFormatter}
          showEndpointMarks={showEndpointMarks}
          orientText
        />

        {!viewOnly && (
          <Html position={position.to3D().toArray() as unknown as Vector3} distanceFactor={0.75} center>
            <DistanceField
              sx={{
                input: { textAlign: 'center', padding: 0, height: '2.25ch' },
                color: '#FF9736', // TODO palette.brand.orange
                fontSize: '36px',
                WebkitTextStroke: '0.75px white',
                minWidth: '3ch',
                width: `${currentText.length + 1}ch`,
                borderRadius: '5px',
                '&:hover': {
                  border: `1px solid #FF9736`,
                  boxShadow: `0 0 2px 1px #FF9736, inset 0 0 2px 1px #FF9736`,
                },
                '&.Mui-focused': {
                  boxShadow: 'none',
                  border: `1px solid #FF9736`,
                  backgroundColor: '#2D2D2D', // TODO palette.background.default
                },
              }}
              slotProps={{ htmlInput: { disableUnderline: true } }}
              onMouseDown={() => setTimeout(() => threeJsDom.leaveScene())} // setTimeout ensures that this event is processed after mousing down in the scene
              onMouseMove={(event) =>
                void (
                  (event.buttons === LEFT_MOUSE_BUTTON || event.buttons === MIDDLE_MOUSE_BUTTON) &&
                  event.stopPropagation()
                )
              }
              onMouseUp={(event) => event.stopPropagation()}
              value={distance}
              onSubmit={onChange}
              onDisplayValueChange={setCurrentText}
            />
          </Html>
        )}
      </>
    );
  },
  isEqual,
);
